import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Smartphone, Home, Keyboard, QrCode, X, CheckCircle2, Loader2, RefreshCw, UserPlus } from "lucide-react";
import { CompassLogo } from "../components/CompassLogo";
import MemberNfcPicker from "../components/MemberNfcPicker";
import SignaturePad from "../components/SignaturePad";
import { useAuth } from "../lib/auth";
import { api, formatApiError, API, authQuery } from "../lib/api";
import { toast } from "sonner";

/**
 * Detect iOS (iPhone / iPad / iPod). iPadOS reports `MacIntel` + touch — keep
 * that in mind. Web NFC is permanently unavailable on every iOS browser.
 */
function detectIOS() {
  if (typeof window === "undefined" || typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  if (/iphone|ipad|ipod/i.test(ua) && !window.MSStream) return true;
  // iPadOS 13+ masquerades as Mac; detect via maxTouchPoints.
  if (/Macintosh/i.test(ua) && navigator.maxTouchPoints && navigator.maxTouchPoints > 1) return true;
  return false;
}

/**
 * Kiosk-style "wait for passport tap" screen.
 *
 * Three ways to start a check-in:
 *  1. NFC tap (Web NFC) — Android Chrome only; the encoded URL navigates the
 *     browser to `/check-in/{token}` (handled by `CheckInPage`).
 *  2. Manual member picker — iOS fallback / damaged-passport fallback.
 *  3. Manual paste — operator pastes/types the token (or full URL).
 */
export default function CheckInScanPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [manual, setManual] = useState("");
  const [visitorOpen, setVisitorOpen] = useState(false);
  const [qrOpen, setQrOpen] = useState(false);
  const [qrError, setQrError] = useState("");
  // After a successful decode we stop the camera and show an inline
  // confirmation card with the brother's profile + a button to continue
  // to the full check-in page. This guarantees the admin ALWAYS sees the
  // scanned brother even if navigation is slow or fails downstream.
  const [qrResolving, setQrResolving] = useState(false);
  const [qrResolved, setQrResolved] = useState(null); // { member, nfc_token }
  // Camera switching: iPads/tablets often expose 2+ cameras. We discover
  // them once via getCameras() and let the operator cycle between them
  // (back ↔ front) with a button on the scanner overlay.
  const [availableCameras, setAvailableCameras] = useState([]);
  const [activeCameraId, setActiveCameraId] = useState(null);
  const qrRegionId = "checkin-qr-reader";
  const html5QrRef = useRef(null);

  const isIOS = useMemo(detectIOS, []);
  const nfcSupported = typeof window !== "undefined" && "NDEFReader" in window;
  const nfcUnavailable = isIOS || !nfcSupported;

  // ----- QR SCANNER via webcam (html5-qrcode) -------------------------
  // When `qrOpen` becomes true we start the camera, decode any QR, resolve
  // the encoded token against `/passport/lookup`, then route to `/check-in/{token}`.
  useEffect(() => {
    if (!qrOpen) return;
    let cancelled = false;
    let scanner = null;
    let started = false;
    (async () => {
      try {
        // Safari/iOS refuses getUserMedia on non-secure origins. Fail
        // loudly instead of hanging silently.
        if (!window.isSecureContext) {
          setQrError(t("checkInScan.camera_requires_https_open_the"));
          return;
        }
        if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
          setQrError(t("checkInScan.this_browser_does_not_support"));
          return;
        }

        const mod = await import("html5-qrcode");
        scanner = new mod.Html5Qrcode(qrRegionId, { verbose: false });
        html5QrRef.current = scanner;

        // Safari desktop often has NO "environment" camera and throws
        // OverconstrainedError instead of falling back. Prefer an
        // explicit deviceId from `getCameras()` when available; fall back
        // to `facingMode: environment`, then to any camera at all.
        let cameraConfig;
        let chosenId = null;
        try {
          const cams = await mod.Html5Qrcode.getCameras();
          if (cams && cams.length) {
            // Persist the camera list so the UI can render a switch button.
            // Only set on the FIRST run to avoid triggering a useEffect
            // re-run loop (the dep array also watches activeCameraId).
            if (availableCameras.length !== cams.length) setAvailableCameras(cams);
            const preferred = activeCameraId
              ? cams.find((c) => c.id === activeCameraId)
              : (cams.find((c) => /back|rear|environment/i.test(c.label || "")) || cams[cams.length - 1]);
            chosenId = preferred?.id || cams[0].id;
            cameraConfig = { deviceId: { exact: chosenId } };
            // Sync state only if the chosen camera is different.
            if (chosenId !== activeCameraId) setActiveCameraId(chosenId);
          }
        } catch (_) { /* ignore, fall through */ }
        if (!cameraConfig) cameraConfig = { facingMode: "environment" };

        await scanner.start(
          cameraConfig,
          { fps: 10, qrbox: { width: 260, height: 260 } },
          (decodedText) => {
            if (cancelled) return;
            cancelled = true;
            // Fire-and-forget. `handleScannedPayload` stops the scanner
            // itself, so we don't await here to avoid holding the camera
            // frame-callback context (Safari-specific freeze).
            handleScannedPayload(decodedText);
          },
          () => { /* ignore frame-level errors */ }
        );
        started = true;
      } catch (err) {
        // Safari returns NotAllowedError (permission denied) or
        // NotFoundError (no camera) — surface the exact reason.
        const name = err && (err.name || err.code) ? (err.name || err.code) : "";
        let msg;
        if (/NotAllowed|PermissionDenied/i.test(name)) {
          msg =t("checkInScan.camera_permission_denied_in_safari");
        } else if (/NotFound|OverConstrained|DevicesNotFound/i.test(name)) {
          msg =t("checkInScan.no_camera_available_connect_a");
        } else if (/NotReadable|TrackStart/i.test(name)) {
          msg =t("checkInScan.camera_is_in_use_by");
        } else {
          msg = t("checkInScan.cameraStartFailed", { name: name || "?" });
        }
        setQrError(msg);
      }
    })();
    return () => {
      cancelled = true;
      // Use the shared helper so unmount + decode-success paths share
      // the same cleanup semantics.
      stopQrScanner();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qrOpen, activeCameraId]);

  // Cycle to the next available camera (back ↔ front on tablets).
  // We stop the current scanner and bump `activeCameraId` so the
  // useEffect above re-mounts with the new camera.
  const switchCamera = () => {
    if (!availableCameras || availableCameras.length < 2) return;
    const idx = availableCameras.findIndex((c) => c.id === activeCameraId);
    const next = availableCameras[(idx + 1) % availableCameras.length];
    setActiveCameraId(next.id);
  };

  // Stop + clear the html5-qrcode scanner defensively. Called whenever we
  // need to release the camera (decode success, modal close, unmount).
  const stopQrScanner = async () => {
    const s = html5QrRef.current;
    html5QrRef.current = null;
    if (!s) return;
    try { await s.stop(); } catch (_) { /* already stopped */ }
    try { s.clear(); } catch (_) { /* ignore */ }
  };

  const handleScannedPayload = async (raw) => {
    const trimmed = (raw || "").trim();
    if (!trimmed) return;
    // Immediately release the camera so Safari / iOS don't hang on the
    // <video> element while we resolve the brother in the background.
    await stopQrScanner();
    setQrResolving(true);
    setQrError("");

    // 1) NFC URL embedded in the QR — fastest path, same as the NFC reader.
    const urlMatch = trimmed.match(/\/check-in\/([A-Za-z0-9_-]+)/);
    const candidateToken = urlMatch ? urlMatch[1] : trimmed;

    // 2) Resolve the brother via the admin lookup endpoint. This also
    //    decrypts the `nfc_token` for us (members created before v1.8
    //    may have the token encrypted at rest) and auto-provisions a
    //    token if the member never had one. Returns a public profile
    //    (name, grade, photo, lodge) so we can show an inline card.
    try {
      const { data } = await api.get("/passport/lookup", { params: { token: candidateToken } });
      const tok = data?.nfc_token;
      if (!tok) {
        throw new Error(t("checkInScan.brother_has_no_active_passport"));
      }
      setQrResolving(false);
      setQrResolved({ member: data, nfc_token: tok });
      // Auto-navigate after a brief confirmation so the admin sees WHO
      // was scanned — mirrors the Messenger-style "scanned!" pulse. The
      // admin can also tap the button to continue instantly.
      setTimeout(() => {
        setQrOpen(false);
        setQrResolved(null);
        navigate(`/check-in/${tok}`);
      }, 900);
    } catch (err) {
      setQrResolving(false);
      // If the lookup failed with a known shape, surface a clear error.
      // Otherwise still try navigating with the raw token: the
      // /attendance/check-in endpoint will return NFC_INVALID which the
      // CheckInPage renders as a readable error card.
      const msg = formatApiError(err);
      const status = err?.response?.status;
      if (status === 403) {
        setQrError(msg); // "brother not in your lodge" — keep modal open
      } else if (status === 404) {
        setQrError(t("checkInScan.passport_not_recognised_ask_the"));
      } else {
        // Network / server error — fall back to the raw token so the
        // admin at least lands on CheckInPage with a clear error.
        setQrOpen(false);
        navigate(`/check-in/${candidateToken}`);
      }
    }
  };

  // Best-effort Web NFC reader: starts a scan if available so a tap on
  // the device fires immediately without leaving the page.
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!nfcSupported) return;
    let cancelled = false;
    (async () => {
      try {
        // eslint-disable-next-line no-undef
        const reader = new NDEFReader();
        await reader.scan();
        reader.onreading = (event) => {
          if (cancelled) return;
          for (const record of event.message.records) {
            try {
              const decoder = new TextDecoder();
              const data = decoder.decode(record.data);
              const m = data.match(/\/check-in\/([A-Za-z0-9_-]+)/);
              if (m) {
                navigate(`/check-in/${m[1]}`);
                return;
              }
            } catch {
              /* ignore */
            }
          }
        };
      } catch {
        /* user declined permission or no hardware — fall back to manual input */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [navigate, nfcSupported]);

  const submitManual = (e) => {
    e.preventDefault();
    const raw = manual.trim();
    if (!raw) return;
    const m = raw.match(/\/check-in\/([A-Za-z0-9_-]+)/) || raw.match(/^([A-Za-z0-9_-]+)$/);
    if (m) navigate(`/check-in/${m[1]}`);
  };

  const onMemberPicked = (member) => {
    if (member?.nfc_token) {
      navigate(`/check-in/${member.nfc_token}`);
    }
  };

  if (user?.role === "superadmin") {
    return (
      <div className="auth-bg flex items-center justify-center" style={{ minHeight: "100vh" }}>
        <div className="gold-card" style={{ maxWidth: 520, padding: "2rem", textAlign: "center" }}>
          <h2 style={{ fontFamily: "Cinzel, serif", letterSpacing: "0.16em" }}>{t("attendance.checkInPage")}</h2>
          <p style={{ marginTop: "1rem", color: "#34322d" }}>
            {t("checkInScan.sign_in_with_a_lodge")}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      className="auth-bg"
      style={{
        minHeight: "100vh",
        padding: "1.5rem",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
      data-testid="checkin-scan-page"
    >
      <div className="gold-card" style={{ maxWidth: 620, width: "100%", padding: "2.5rem 2rem", textAlign: "center" }}>
        <div style={{ display: "flex", justifyContent: "center", marginBottom: "1rem" }}>
          <CompassLogo size={64} />
        </div>
        <div
          className="kv-label"
          style={{ fontSize: "0.7rem", letterSpacing: "0.22em", color: "#9a7530", marginBottom: "1.4rem" }}
        >
          {t("attendance.checkInPage")} · NFC
        </div>

        {nfcUnavailable ? (
          <>
            <Smartphone
              size={56}
              color="#9a7530"
              style={{ display: "block", margin: "0 auto 0.6rem", opacity: 0.5 }}
            />
            <h2
              style={{
                fontFamily: "Cinzel, serif",
                fontSize: "1.1rem",
                color: "#0d1b2a",
                letterSpacing: "0.06em",
                marginBottom: "0.4rem",
              }}
              data-testid="checkin-manual-mode-title"
            >
              {t("attendance.checkInWaiting")}
            </h2>
          </>
        ) : (
          <>
            <Smartphone
              size={88}
              color="#9a7530"
              style={{ display: "block", margin: "0 auto 1.2rem", animation: "pulse-tap 1.6s ease-in-out infinite" }}
            />
            <h2
              style={{
                fontFamily: "Cinzel, serif",
                fontSize: "1.4rem",
                color: "#0d1b2a",
                letterSpacing: "0.06em",
                marginBottom: "0.4rem",
              }}
            >
              {t("attendance.checkInWaiting")}
            </h2>
          </>
        )}

        <div className="gold-divider my-5" />

        <div style={{ marginBottom: "0.7rem" }}>
          <MemberNfcPicker onSelect={onMemberPicked} testId="manual-member-picker" includeChildren />
          <div className="kv-label" style={{ fontSize: "0.62rem", marginTop: "0.4rem", color: "#9a7530" }}>
            {t("attendance.manualPickHint")}
          </div>
        </div>

        <button
          type="button"
          className="btn-primary-gold flex items-center gap-2"
          onClick={() => { setQrError(""); setQrOpen(true); }}
          data-testid="scan-qr-btn"
          style={{ margin: "0.4rem auto 0.2rem", padding: "0.65rem 1.1rem" }}
        >
          <QrCode size={16} />
          {t("checkInScan.scan_digital_passport_qr")}
        </button>
        <div className="kv-label" style={{ fontSize: "0.6rem", color: "#9a7530", marginBottom: "0.4rem" }}>
          {t("checkInScan.use_the_laptop_tablet_camera")}
        </div>

        <button
          type="button"
          className="btn-outline-gold flex items-center gap-2"
          onClick={() => setVisitorOpen(true)}
          data-testid="add-visitor-btn"
          style={{ margin: "1rem auto 0.2rem", padding: "0.65rem 1.1rem" }}
        >
          <UserPlus size={15} />
          {t("checkInScan.addVisitorBtn")}
        </button>
        <div className="kv-label" style={{ fontSize: "0.6rem", color: "#9a7530", marginBottom: "0.4rem" }}>
          {t("checkInScan.addVisitorHint")}
        </div>
        <div className="kv-label" style={{ fontSize: "0.62rem", marginTop: "0.6rem", color: "#9a7530" }}>
          <Keyboard size={11} style={{ display: "inline", marginRight: 4, verticalAlign: "-2px" }} />
          {t("checkInScan.tip_keep_this_page_open")}
        </div>

        <div className="flex justify-center mt-6">
          <button type="button" className="btn-outline-gold flex items-center gap-1" onClick={() => navigate("/attendance")} data-testid="scan-back-btn">
            <Home size={12} /> {t("checkInScan.back_to_app")}
          </button>
        </div>
      </div>

      {/* QR scanner modal */}
      {qrOpen && (
        <div
          style={{
            position: "fixed", inset: 0, background: "rgba(13,27,42,0.92)",
            zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center",
            padding: "1rem",
          }}
          data-testid="qr-scanner-modal"
        >
          <div className="gold-card" style={{ maxWidth: 420, width: "100%", padding: "1.2rem", textAlign: "center", background: "#0d1b2a", border: "1px solid #c9a84c" }}>
            <div className="flex items-center justify-between mb-3">
              <h3 style={{ fontFamily: "Cinzel, serif", color: "#e8c97a", letterSpacing: "0.12em", margin: 0 }}>
                {t("checkInScan.scan_qr")}
              </h3>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                {availableCameras.length > 1 ? (
                  <button
                    type="button"
                    onClick={switchCamera}
                    title={t("checkInScan.switch_camera_front_back")}
                    style={{
                      background: "transparent",
                      color: "#e8c97a",
                      border: "1px solid #c9a84c",
                      borderRadius: 4,
                      padding: "5px 10px",
                      fontSize: "0.7rem",
                      letterSpacing: "0.12em",
                      cursor: "pointer",
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 6,
                    }}
                    data-testid="qr-scanner-switch-camera"
                  >
                    <RefreshCw size={13} />
                    {t("checkInScan.flip")}
                  </button>
                ) : null}
                <button type="button" onClick={() => setQrOpen(false)} style={{ background: "transparent", color: "#e8c97a", border: "none", cursor: "pointer" }} data-testid="qr-scanner-close">
                  <X size={22} />
                </button>
              </div>
            </div>
            <div id={qrRegionId} style={{ width: "100%", minHeight: 280, borderRadius: 6, overflow: "hidden", background: "#000" }} />
            {qrError ? (
              <p style={{ color: "#ef4444", fontSize: "0.8rem", marginTop: "0.6rem" }}>{qrError}</p>
            ) : (
              <p style={{ color: "#c9a84c", fontSize: "0.72rem", marginTop: "0.6rem", fontStyle: "italic" }}>
                {t("checkInScan.aim_at_the_brother_s")}
              </p>
            )}
          </div>
        </div>
      )}
      {visitorOpen && <VisitorModal onClose={() => setVisitorOpen(false)} />}
      <style>{`
        @keyframes pulse-tap {
          0%, 100% { transform: scale(1); opacity: 1; }
          50% { transform: scale(1.08); opacity: 0.8; }
        }
      `}</style>
    </div>
  );
}

function VisitorModal({ onClose }) {
  const { t, i18n } = useTranslation();
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({ first_name: "", last_name: "", home_lodge_name: "", home_lodge_number: "", grade: "" });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const fieldsOk = form.first_name.trim() && form.last_name.trim() && form.home_lodge_name.trim();

  const save = async (signatureDataUrl) => {
    setSaving(true);
    try {
      const { data: act } = await api.get("/attendance/active-event");
      if (!act?.event?.id) {
        toast.error(t("checkInScan.noActiveEvent"));
        setSaving(false);
        return;
      }
      await api.post(`/events/${act.event.id}/visitors`, { ...form, signature_data_url: signatureDataUrl });
      toast.success(t("checkInScan.visitorSaved"));
      onClose();
    } catch (err) {
      toast.error(formatApiError(err));
      setSaving(false);
    }
  };

  return (
    <div className="dialog-overlay" onClick={onClose} data-testid="visitor-modal">
      <div className="dialog-panel" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 540 }}>
        <h3 className="section-title flex items-center gap-2">
          <UserPlus size={15} /> {t("checkInScan.addVisitorTitle")}
        </h3>
        {step === 1 ? (
          <>
            <div className="grid grid-cols-2 gap-3 mt-3">
              <div>
                <label className="label-lodge">{t("checkInScan.visitorFirstName")} *</label>
                <input className="input-lodge" value={form.first_name} onChange={set("first_name")} data-testid="visitor-first-name" />
              </div>
              <div>
                <label className="label-lodge">{t("checkInScan.visitorLastName")} *</label>
                <input className="input-lodge" value={form.last_name} onChange={set("last_name")} data-testid="visitor-last-name" />
              </div>
              <div>
                <label className="label-lodge">{t("checkInScan.visitorLodgeName")} *</label>
                <input className="input-lodge" value={form.home_lodge_name} onChange={set("home_lodge_name")} data-testid="visitor-lodge-name" />
              </div>
              <div>
                <label className="label-lodge">{t("checkInScan.visitorLodgeNumber")}</label>
                <input className="input-lodge" value={form.home_lodge_number} onChange={set("home_lodge_number")} data-testid="visitor-lodge-number" />
              </div>
              <div>
                <label className="label-lodge">{t("checkInScan.visitorGrade")}</label>
                <input className="input-lodge" value={form.grade} onChange={set("grade")} data-testid="visitor-grade" />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <button className="btn-outline-gold" onClick={onClose}>{t("common.cancel")}</button>
              <button
                className="btn-primary-gold"
                disabled={!fieldsOk}
                onClick={() => setStep(2)}
                data-testid="visitor-next-btn"
              >
                {t("checkInScan.visitorSign")} →
              </button>
            </div>
          </>
        ) : (
          <div className="mt-3" data-testid="visitor-signature-step">
            <p className="kv-label" style={{ marginBottom: 8 }}>
              {form.first_name} {form.last_name} — {form.home_lodge_name}
              {form.home_lodge_number ? ` No. ${form.home_lodge_number}` : ""}
            </p>
            {saving ? (
              <div className="flex items-center gap-2 py-6 justify-center"><Loader2 size={16} className="spin" /></div>
            ) : (
              <SignaturePad
                width={460}
                height={180}
                lang={i18n.language}
                onSave={save}
                onCancel={() => setStep(1)}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
}
